import {
  ExistingPage,
  LoadPagesInModulesResponse,
  ModuleListItem,
} from '../../web/components/PageHelper/Screens/ExistingPages/ExistingPageListing/types';

export const sortExistingPagesAlphabetically = (
  a: ExistingPage,
  b: ExistingPage
): number => {
  const titleA = a.title.toUpperCase();
  const titleB = b.title.toUpperCase();

  if (titleA < titleB) {
    return -1;
  }

  if (titleA > titleB) {
    return 1;
  }

  return 0;
};

export const extractExistingPagesGroupings = (
  response: LoadPagesInModulesResponse,
  currentModule: string | null
): ExistingPage[] => {
  const pagesNotInModules = response.notInModules
    .slice(0)
    .sort(sortExistingPagesAlphabetically);
  const pageIdsWithModulesSet = new Set();
  const pagesWithModules: ExistingPage[] = [];

  response.moduleList
    .flatMap((moduleList: ModuleListItem) => moduleList.pages)
    .forEach((page) => {
      if (!pageIdsWithModulesSet.has(page.id)) {
        pageIdsWithModulesSet.add(page.id);
        pagesWithModules.push(page);
      }
    });

  const pagesWithModulesNotContainingCurrentModule = pagesWithModules
    .filter((page) => !page.modules.includes(currentModule || ''))
    .sort(sortExistingPagesAlphabetically);
  const pagesWithModulesContainingCurrentModule = pagesWithModules
    .filter((page) => page.modules.includes(currentModule || ''))
    .sort(sortExistingPagesAlphabetically);

  return pagesNotInModules.concat(
    pagesWithModulesNotContainingCurrentModule,
    pagesWithModulesContainingCurrentModule
  );
};

type TreeNode = {
  path: string;
  page: ExistingPage | null;
  children: TreeNode[];
};

// DFS over the tree to get subpageLevel for each path
// Only increase subpageLevel when there is a page in the node.
const dfsSubpageTree = (
  node: TreeNode,
  subpageLevel: number,
  result: ExistingPage[]
): ExistingPage[] => {
  if (node.page) {
    result.push({ ...node.page, subpageLevel });
  }
  node.children.forEach((childNode) => {
    dfsSubpageTree(
      childNode,
      node.page ? subpageLevel + 1 : subpageLevel,
      result
    );
  });
  return result;
};

export const getPagesInSubpageStructure = (
  pages: ExistingPage[]
): ExistingPage[] => {
  const root: TreeNode = {
    path: 'courses',
    page: null,
    children: [],
  };

  // Break each subpath into nodes and build the tree
  pages.forEach((page) => {
    const paths = page.path.split('courses/')[1].split('/');

    // Add page paths to the tree
    let prevNode = root;
    paths.forEach((path, index) => {
      let node = prevNode.children.find((x) => x.path === path);
      if (!node) {
        node = {
          path,
          page: null,
          children: [],
        };
        prevNode.children.push(node);
      }
      if (index === paths.length - 1) {
        node.page = page;
      }
      prevNode = node;
    });
  });

  return dfsSubpageTree(root, 0, []);
};

export const geFullPath = (
  institutionPrefix: string | null,
  courseCode: string,
  pageSlug: string
): string => {
  if (institutionPrefix) {
    return `${institutionPrefix}/courses/${courseCode}/${pageSlug}`;
  } else {
    return `courses/${courseCode}/${pageSlug}`;
  }
};

export const pageSlugFromPath = (path: string): string | null => {
  // remove leading and trailing "/"
  const segments = path.replace(/^\/|\/$/g, '').split('/');
  if (segments.length < 2) {
    return null;
  }
  // no institution prefix
  if (segments[0] === 'courses') {
    return segments.slice(2).join('/');
  }
  if (segments[1] !== 'courses') {
    return null;
  }
  if (segments.length < 3) {
    return null;
  }
  // with institution prefix
  return segments.slice(3).join('/');
};

export const slugFromCoursePath = (path: string): string => {
  const paths = path.split('/');
  return paths[paths.length - 1];
};
